import { Button, DatePicker, Form, FormInstance, Input, Select } from 'antd';
import { ContractInterface } from 'contract/type';
import dayjs from 'dayjs';
import React, { useState, useEffect, useCallback } from 'react';
import { CompanyInterface } from 'type/companyType';
import { PlatformInterface } from 'type/platformType';
import * as Component from '../../../../../../shared/components';
import { LinkOutlined } from '@ant-design/icons';
import { companyApiService } from 'service/api/companyApiService';
import { contractService } from 'content/service';
import { debounce } from 'lodash';
import { COMPANY_COUNTRIES, CONTINENTS } from 'constants/constant';

const { Option } = Select;

interface ContractDetailProps {
  form: FormInstance;
  companyList: CompanyInterface[];
  platformList: PlatformInterface[];
  contract?: ContractInterface;
  actionTitle?: string;
  handleOnClickButtonClick?: () => void;
  readonly?: boolean;
}

const contentTypeOptions = [
  'DRAMA',
  'MOVIE',
  'VARIETY',
  'DOCUMENTARY',
  'NEWS',
  'GENERAL_ENTERTAINMENT',
  'ANIMATION',
  'MUSIC',
];
const serviceTypeOptions = ['SERVICE', 'FILE_TRANSFER', 'SIGN', 'SERVICE_EXPIRED'];

// Territory options: includes both continents and countries
const territoryOptions = [
  ...CONTINENTS.map(continent => ({
    value: continent.value,
    label: continent.labelEn,
    labelKo: continent.label,
    type: 'continent' as const,
  })),
  ...COMPANY_COUNTRIES.map(country => ({
    value: country.value,
    label: country.labelEn,
    labelKo: country.label,
    type: 'country' as const,
  })),
];

const ContractDetail: React.FC<ContractDetailProps> = ({
  form,
  contract,
  actionTitle,
  companyList: _companyList,
  handleOnClickButtonClick,
  readonly = false,
}) => {
  const [searchCompanies, setSearchCompanies] = useState<CompanyInterface[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchContracts, setSearchContracts] = useState<ContractInterface[]>([]);
  const [contractSearchLoading, setContractSearchLoading] = useState(false);

  // labelCol/wrapperCol configuration
  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
    labelAlign: 'left' as const,
  };

  // Company search function (with debounce)
  const debouncedSearchCompanies = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setSearchCompanies([]);
        return;
      }

      setSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await companyApiService.fetch(queryParams);
        setSearchCompanies(result.results);
      } catch (error) {
        console.error('Company search failed:', error);
        setSearchCompanies([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    []
  );

  // Contract search function (with debounce)
  const debouncedSearchContracts = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setSearchContracts([]);
        return;
      }

      setContractSearchLoading(true);
      try {
        const contracts = await contractService.searchContracts(searchTerm);
        // Exclude the contract currently being edited
        const filteredContracts = contracts.filter(c => c.id !== contract?.id);
        setSearchContracts(filteredContracts);
      } catch (error) {
        console.error('Contract search failed:', error);
        setSearchContracts([]);
      } finally {
        setContractSearchLoading(false);
      }
    }, 300),
    [contract?.id]
  );

  // Load initial licensor information
  useEffect(() => {
    const loadInitialLicensor = async () => {
      if (contract?.licensor && typeof contract.licensor === 'number') {
        try {
          const licensorData = await companyApiService.fetchLicensorById(contract.licensor);
          // Add to search results to display as selected
          setSearchCompanies([licensorData]);
        } catch (error) {
          console.error('Failed to load licensor:', error);
        }
      }
    };

    loadInitialLicensor();
  }, [contract?.licensor]);

  // Load initial reference contracts
  useEffect(() => {
    const loadInitialReferenceContracts = async () => {
      if (contract?.reference_contract_ids && contract.reference_contract_ids.length > 0) {
        try {
          const referenceContracts = await contractService.fetchByIds(
            contract.reference_contract_ids
          );
          setSearchContracts(referenceContracts);
        } catch (error) {
          console.error('Failed to load reference contracts:', error);
        }
      }
    };

    loadInitialReferenceContracts();
  }, [contract?.reference_contract_ids]);

  // Company option rendering function
  const renderCompanyOption = (company: CompanyInterface) => {
    const titlePart = company.title_en ? `${company.title} (${company.title_en})` : company.title;
    const countryPart = company.country ? ` / ${company.country}` : '';
    const label = titlePart + countryPart;

    return (
      <Option key={company.id} value={company.id} title={label}>
        <span style={{ fontWeight: 500 }}>{label}</span>
      </Option>
    );
  };

  // Contract option rendering function
  const renderContractOption = (contract: ContractInterface) => {
    const titlePart = contract.title_en
      ? `${contract.title} (${contract.title_en})`
      : contract.title;
    const label = `#${contract.id} ${titlePart}`;

    return (
      <Option key={contract.id} value={contract.id} title={label}>
        <span style={{ fontWeight: 500 }}>{label}</span>
      </Option>
    );
  };

  return (
    <div>
      <div className="contract-details-page">
        {!readonly && <h1>Contract: {contract?.title}</h1>}
        <Form
          form={form}
          layout="horizontal"
          className="contract-details-form"
          labelCol={formItemLayout.labelCol}
          wrapperCol={formItemLayout.wrapperCol}
          labelAlign={formItemLayout.labelAlign}
        >
          <Form.Item
            name="reference_contract_ids"
            label="Reference Contract ID"
            initialValue={contract?.reference_contract_ids}
          >
            <Select
              disabled={readonly}
              mode="multiple"
              showSearch
              placeholder="Search and select reference contracts..."
              filterOption={false}
              onSearch={debouncedSearchContracts}
              loading={contractSearchLoading}
              notFoundContent={contractSearchLoading ? 'Searching...' : 'No contracts found'}
              style={{ width: '100%' }}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            >
              {searchContracts.map(renderContractOption)}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="Contract Name (Original Language)"
            initialValue={contract?.title}
            rules={[{ required: true, message: 'Please enter a contract title' }]}
          >
            <Input disabled={readonly} />
          </Form.Item>

          <Form.Item
            name="title_en"
            label="Contract Name (EN)"
            initialValue={contract?.title_en}
            rules={[{ required: true, message: 'Please enter a contract title (EN)' }]}
          >
            <Input disabled={readonly} />
          </Form.Item>

          <Form.Item
            name="licensor"
            label="Content Provider"
            initialValue={contract?.licensor}
            rules={[{ required: true, message: 'Please select a licensor' }]}
          >
            <Select
              disabled={readonly}
              showSearch
              placeholder="Search and select content provider..."
              filterOption={false}
              onSearch={debouncedSearchCompanies}
              loading={searchLoading}
              notFoundContent={searchLoading ? 'Searching...' : 'No companies found'}
              style={{ width: '100%' }}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            >
              {searchCompanies.map(renderCompanyOption)}
            </Select>
          </Form.Item>

          <Form.Item
            name="service_type"
            label="Service Type"
            initialValue={contract?.service_type}
            rules={[{ required: true, message: 'Please select a service type' }]}
          >
            <Select disabled={readonly}>
              {serviceTypeOptions.map(option => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="signed_date"
            label="Signed Date"
            initialValue={contract?.signed_date || null}
            getValueFromEvent={(_date, dateString) => dateString || null}
            getValueProps={value => ({
              value: value ? dayjs(value, 'YYYY-MM-DD') : null,
            })}
            rules={[{ required: true, message: 'Please select a signed date' }]}
          >
            <DatePicker disabled={readonly} format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="contract_start_date"
            label="Contract Start Date"
            initialValue={contract?.contract_start_date || null}
            getValueFromEvent={(_date, dateString) => dateString || null}
            getValueProps={value => ({
              value: value ? dayjs(value, 'YYYY-MM-DD') : null,
            })}
            rules={[{ required: true, message: 'Please select a contract start date' }]}
          >
            <DatePicker disabled={readonly} format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="contract_expire_date"
            label="Contract Expire Date"
            initialValue={contract?.contract_expire_date || null}
            getValueFromEvent={(_date, dateString) => dateString || null}
            getValueProps={value => ({
              value: value ? dayjs(value, 'YYYY-MM-DD') : null,
            })}
            rules={[{ required: true, message: 'Please select a contract expire date' }]}
          >
            <DatePicker disabled={readonly} format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="contract_file_link"
            label="Contract File Link"
            initialValue={contract?.contract_file_link}
            rules={[{ required: true, message: 'Please enter a contract file link' }]}
          >
            <Input
              disabled={readonly}
              placeholder="Enter contract file URL"
              addonAfter={
                contract?.contract_file_link ? (
                  <Button
                    type="link"
                    icon={<LinkOutlined />}
                    onClick={() => {
                      const currentValue = form.getFieldValue('contract_file_link');
                      if (currentValue) {
                        window.open(currentValue, '_blank');
                      }
                    }}
                    title="Open contract file"
                    style={{ padding: 0, height: 'auto' }}
                  >
                    View
                  </Button>
                ) : null
              }
            />
          </Form.Item>

          <Form.Item
            name="territory_whitelist"
            label="Territory"
            initialValue={contract?.territory_whitelist}
            rules={[{ required: true, message: 'Please select at least one territory' }]}
          >
            <Select
              mode="multiple"
              disabled={readonly}
              showSearch
              placeholder="Select territories"
              filterOption={(input, option) => {
                const searchTerm = input.toLowerCase();
                const territory = territoryOptions.find(t => t.value === option?.value);
                if (!territory) return false;

                const label = territory.label.toLowerCase();
                const labelKo = territory.labelKo.toLowerCase();
                const value = territory.value.toLowerCase();

                const isMatch =
                  label.includes(searchTerm) ||
                  labelKo.includes(searchTerm) ||
                  value.includes(searchTerm);

                return isMatch;
              }}
              style={{ width: '100%' }}
            >
              {territoryOptions.map(territory => (
                <Select.Option key={territory.value} value={territory.value}>
                  {territory.type === 'continent' ? '🌍' : '🏳️'} {territory.value} -{' '}
                  {territory.labelKo} ({territory.label})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="territory_blacklist"
            label="Territory Blacklist"
            initialValue={contract?.territory_blacklist}
          >
            <Select
              mode="multiple"
              disabled={readonly}
              showSearch
              placeholder="Select territories to exclude"
              filterOption={(input, option) => {
                const searchTerm = input.toLowerCase();
                const territory = territoryOptions.find(t => t.value === option?.value);
                if (!territory) return false;

                const label = territory.label.toLowerCase();
                const labelKo = territory.labelKo.toLowerCase();
                const value = territory.value.toLowerCase();

                const isMatch =
                  label.includes(searchTerm) ||
                  labelKo.includes(searchTerm) ||
                  value.includes(searchTerm);

                return isMatch;
              }}
              style={{ width: '100%' }}
            >
              {territoryOptions.map(territory => (
                <Select.Option key={territory.value} value={territory.value}>
                  {territory.type === 'continent' ? '🌍' : '🏳️'} {territory.value} -{' '}
                  {territory.labelKo} ({territory.label})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="sns_territory_whitelist"
            label="SNS Territory"
            initialValue={contract?.sns_territory_whitelist}
          >
            <Select
              mode="multiple"
              disabled={readonly}
              showSearch
              placeholder="Select SNS territories"
              filterOption={(input, option) => {
                const searchTerm = input.toLowerCase();
                const territory = territoryOptions.find(t => t.value === option?.value);
                if (!territory) return false;

                const label = territory.label.toLowerCase();
                const labelKo = territory.labelKo.toLowerCase();
                const value = territory.value.toLowerCase();

                const isMatch =
                  label.includes(searchTerm) ||
                  labelKo.includes(searchTerm) ||
                  value.includes(searchTerm);

                return isMatch;
              }}
              style={{ width: '100%' }}
            >
              {territoryOptions.map(territory => (
                <Select.Option key={territory.value} value={territory.value}>
                  {territory.type === 'continent' ? '🌍' : '🏳️'} {territory.value} -{' '}
                  {territory.labelKo} ({territory.label})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="note" label="Note" initialValue={contract?.note}>
            <Input.TextArea disabled={readonly} />
          </Form.Item>
        </Form>
      </div>
      {!readonly && (
        <div className="people-detail-update-button">
          <Component.ColorButtonComponent
            text={actionTitle || 'Update'}
            onClick={handleOnClickButtonClick}
          />
        </div>
      )}
    </div>
  );
};

export default ContractDetail;
